import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useUserContext } from 'src/context/user/UserContext'
import type { NeoChatMessage } from 'src/types/neoChat.types'
import {
  clearDemoApprovalResolutions,
  clearDemoInsightStates,
} from './demoSessionState'
import { DEFAULT_DEMO_SET_ID, DEMO_SETS, findDemoSetById } from './sets'
import type { DemoSession, DemoSet } from './types'

const ENABLED_KEY = 'neo_demo_mode_enabled'
const ACTIVE_SET_KEY = 'neo_demo_active_set_id'
const SESSIONS_KEY = 'neo_demo_sessions'
const PERSONA_OVERRIDES_KEY = 'neo_demo_persona_overrides'
const ALLOWED_EMAIL_DOMAIN = '@traba.work'
const MAX_SESSIONS_PER_SET = 50

type PersonaOverride = { firstName: string; lastName: string }
type PersonaOverrideMap = Record<string, PersonaOverride>

type DemoModeContextValue = {
  /** True only when (1) the flag is set, (2) the user is Traba, and (3) an active set is resolved. */
  isDemoMode: boolean
  /** True if the current user is allowed to see/toggle demo mode at all. */
  isAllowed: boolean
  /** All demo sets available to choose from. */
  availableSets: readonly DemoSet[]
  /** The currently selected set (null if none selected or the stored id is unknown). */
  activeSet: DemoSet | null
  /** Raw active set id from storage (may be null or an unknown id). */
  activeSetId: string | null
  /** Demo chat-history entries for the currently active set, newest first. */
  demoSessions: readonly DemoSession[]
  /** Total count of stored demo sessions across every set, for UI displays. */
  totalDemoSessionCount: number
  /**
   * Resolved persona for the active set, with this priority:
   *   override (set in UI) → set.userPersona default → null.
   * Null means the profile UI should fall back to the real user profile.
   */
  activePersona: PersonaOverride | null
  /** Raw user-set persona overrides, keyed by set id. */
  personaOverrides: Readonly<PersonaOverrideMap>
  setEnabled: (next: boolean) => void
  setActiveSetId: (id: string) => void
  addDemoSession: (session: DemoSession) => void
  removeDemoSession: (id: string) => void
  /** Replace a session's stored message transcript (called after each beat). */
  updateDemoSessionMessages: (id: string, messages: NeoChatMessage[]) => void
  /** Wipe every persisted demo chat session across all sets. */
  clearDemoSessions: () => void
  setPersonaOverride: (setId: string, persona: PersonaOverride) => void
  clearPersonaOverride: (setId: string) => void
}

const DemoModeContext = createContext<DemoModeContextValue>({
  isDemoMode: false,
  isAllowed: false,
  availableSets: DEMO_SETS,
  activeSet: null,
  activeSetId: null,
  demoSessions: [],
  totalDemoSessionCount: 0,
  activePersona: null,
  personaOverrides: {},
  setEnabled: () => null,
  setActiveSetId: () => null,
  addDemoSession: () => null,
  removeDemoSession: () => null,
  updateDemoSessionMessages: () => null,
  clearDemoSessions: () => null,
  setPersonaOverride: () => null,
  clearPersonaOverride: () => null,
})

function readStoredEnabled(): boolean {
  try {
    return localStorage.getItem(ENABLED_KEY) === 'true'
  } catch {
    return false
  }
}

function writeStoredEnabled(value: boolean): void {
  try {
    if (value) {
      localStorage.setItem(ENABLED_KEY, 'true')
    } else {
      localStorage.removeItem(ENABLED_KEY)
    }
  } catch {
    // localStorage can fail in private browsing or with quota issues —
    // demo mode falls back to off, which is the safe default.
  }
}

function readStoredActiveSetId(): string | null {
  try {
    return localStorage.getItem(ACTIVE_SET_KEY)
  } catch {
    return null
  }
}

function writeStoredActiveSetId(value: string | null): void {
  try {
    if (value === null) {
      localStorage.removeItem(ACTIVE_SET_KEY)
    } else {
      localStorage.setItem(ACTIVE_SET_KEY, value)
    }
  } catch {
    // see comment on writeStoredEnabled
  }
}

// JSON.stringify converts Date → ISO string, but the chat renderer expects
// real Date instances on `message.timestamp`. Re-hydrate on read so restored
// sessions look indistinguishable from live ones.
function rehydrateMessages(raw: unknown): NeoChatMessage[] | undefined {
  if (!Array.isArray(raw)) {
    return undefined
  }
  return raw.map((m: unknown) => {
    const message = m as NeoChatMessage & { timestamp: unknown }
    const ts = message.timestamp
    return {
      ...message,
      timestamp: ts instanceof Date ? ts : new Date(ts as string | number),
    }
  })
}

function readStoredSessions(): DemoSession[] {
  try {
    const raw = localStorage.getItem(SESSIONS_KEY)
    if (!raw) {
      return []
    }
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) {
      return []
    }
    return parsed
      .filter(
        (entry): entry is DemoSession =>
          typeof entry === 'object' &&
          entry !== null &&
          typeof (entry as DemoSession).id === 'string' &&
          typeof (entry as DemoSession).setId === 'string' &&
          typeof (entry as DemoSession).scriptId === 'string',
      )
      .map((entry) => ({
        ...entry,
        messages: rehydrateMessages(entry.messages),
      }))
  } catch {
    return []
  }
}

function writeStoredSessions(value: DemoSession[]): void {
  try {
    localStorage.setItem(SESSIONS_KEY, JSON.stringify(value))
  } catch {
    // see comment on writeStoredEnabled
  }
}

function readStoredPersonaOverrides(): PersonaOverrideMap {
  try {
    const raw = localStorage.getItem(PERSONA_OVERRIDES_KEY)
    if (!raw) {
      return {}
    }
    const parsed: unknown = JSON.parse(raw)
    if (
      parsed === null ||
      typeof parsed !== 'object' ||
      Array.isArray(parsed)
    ) {
      return {}
    }
    const result: PersonaOverrideMap = {}
    for (const [setId, value] of Object.entries(parsed)) {
      if (
        value !== null &&
        typeof value === 'object' &&
        typeof (value as PersonaOverride).firstName === 'string' &&
        typeof (value as PersonaOverride).lastName === 'string'
      ) {
        result[setId] = {
          firstName: (value as PersonaOverride).firstName,
          lastName: (value as PersonaOverride).lastName,
        }
      }
    }
    return result
  } catch {
    return {}
  }
}

function writeStoredPersonaOverrides(value: PersonaOverrideMap): void {
  try {
    localStorage.setItem(PERSONA_OVERRIDES_KEY, JSON.stringify(value))
  } catch {
    // see comment on writeStoredEnabled
  }
}

export function useDemoMode(): DemoModeContextValue {
  return useContext(DemoModeContext)
}

export function DemoModeProvider({ children }: { children: JSX.Element }) {
  const { state } = useUserContext()
  const email = state.userProfile?.email ?? ''
  const isAllowed = email.toLowerCase().endsWith(ALLOWED_EMAIL_DOMAIN)

  const [enabled, setEnabledState] = useState<boolean>(() =>
    readStoredEnabled(),
  )
  const [activeSetId, setActiveSetIdState] = useState<string | null>(() =>
    readStoredActiveSetId(),
  )
  const [allSessions, setAllSessions] = useState<DemoSession[]>(() =>
    readStoredSessions(),
  )
  const [personaOverrides, setPersonaOverridesState] =
    useState<PersonaOverrideMap>(() => readStoredPersonaOverrides())

  // Hard guard: any time the user is not a Traba employee, demo mode is off
  // and all stored flags + sessions are cleared. Covers login as a different
  // account in the same tab and the rare case of a stale flag surviving a
  // swap. Gated on `!isLoadingUser` so we don't wipe on initial mount when
  // the user profile hasn't loaded yet (email = '' makes isAllowed = false
  // briefly before the real value arrives).
  useEffect(() => {
    if (state.isLoadingUser) {
      return
    }
    if (!isAllowed) {
      if (enabled) {
        writeStoredEnabled(false)
        setEnabledState(false)
      }
      if (activeSetId !== null) {
        writeStoredActiveSetId(null)
        setActiveSetIdState(null)
      }
      if (allSessions.length > 0) {
        writeStoredSessions([])
        setAllSessions([])
      }
      if (Object.keys(personaOverrides).length > 0) {
        writeStoredPersonaOverrides({})
        setPersonaOverridesState({})
      }
      clearDemoApprovalResolutions()
      clearDemoInsightStates()
    }
  }, [
    state.isLoadingUser,
    isAllowed,
    enabled,
    activeSetId,
    allSessions.length,
    personaOverrides,
  ])

  // Clear all demo state when the user transitions from logged-in to
  // logged-out, so a re-login (potentially as a different user) starts
  // clean. Important: only fire on the transition, not on the initial
  // mount when `state.isUserLoggedIn` is still `false` because the user
  // profile hasn't loaded yet — otherwise every page reload wipes demo
  // mode before the user is even resolved.
  const prevLoggedInRef = useRef<boolean | null>(null)
  useEffect(() => {
    if (state.isLoadingUser) {
      return
    }
    const wasLoggedIn = prevLoggedInRef.current
    prevLoggedInRef.current = state.isUserLoggedIn
    if (wasLoggedIn === true && !state.isUserLoggedIn) {
      writeStoredEnabled(false)
      writeStoredActiveSetId(null)
      writeStoredSessions([])
      writeStoredPersonaOverrides({})
      clearDemoApprovalResolutions()
      clearDemoInsightStates()
      setEnabledState(false)
      setActiveSetIdState(null)
      setAllSessions([])
      setPersonaOverridesState({})
    }
  }, [state.isLoadingUser, state.isUserLoggedIn])

  const setEnabled = useCallback(
    (next: boolean) => {
      if (next && !isAllowed) {
        return
      }
      writeStoredEnabled(next)
      setEnabledState(next)
      // Auto-select the default set on first enable so the toggle is
      // immediately useful. Preserves any prior selection.
      if (next && activeSetId === null) {
        writeStoredActiveSetId(DEFAULT_DEMO_SET_ID)
        setActiveSetIdState(DEFAULT_DEMO_SET_ID)
      }
    },
    [isAllowed, activeSetId],
  )

  const setActiveSetId = useCallback(
    (id: string) => {
      if (!isAllowed) {
        return
      }
      writeStoredActiveSetId(id)
      setActiveSetIdState(id)
    },
    [isAllowed],
  )

  const addDemoSession = useCallback((session: DemoSession) => {
    setAllSessions((prev) => {
      // De-dupe by id (in case of double-add) and cap per-set length so an
      // overzealous demoer doesn't fill localStorage. Newest first.
      const withoutDupe = prev.filter((s) => s.id !== session.id)
      const next = [session, ...withoutDupe]
      const perSetCounts = new Map<string, number>()
      const trimmed: DemoSession[] = []
      for (const entry of next) {
        const count = perSetCounts.get(entry.setId) ?? 0
        if (count < MAX_SESSIONS_PER_SET) {
          trimmed.push(entry)
          perSetCounts.set(entry.setId, count + 1)
        }
      }
      writeStoredSessions(trimmed)
      return trimmed
    })
  }, [])

  const removeDemoSession = useCallback((id: string) => {
    setAllSessions((prev) => {
      const next = prev.filter((s) => s.id !== id)
      writeStoredSessions(next)
      return next
    })
  }, [])

  const updateDemoSessionMessages = useCallback(
    (id: string, messages: NeoChatMessage[]) => {
      setAllSessions((prev) => {
        let changed = false
        const next = prev.map((s) => {
          if (s.id !== id) {
            return s
          }
          changed = true
          return {
            ...s,
            messages,
            updatedAt: new Date().toISOString(),
          }
        })
        if (!changed) {
          return prev
        }
        writeStoredSessions(next)
        return next
      })
    },
    [],
  )

  const clearDemoSessions = useCallback(() => {
    writeStoredSessions([])
    setAllSessions([])
    clearDemoApprovalResolutions()
    clearDemoInsightStates()
  }, [])

  const setPersonaOverride = useCallback(
    (setId: string, persona: PersonaOverride) => {
      setPersonaOverridesState((prev) => {
        const next = { ...prev, [setId]: persona }
        writeStoredPersonaOverrides(next)
        return next
      })
    },
    [],
  )

  const clearPersonaOverride = useCallback((setId: string) => {
    setPersonaOverridesState((prev) => {
      if (!(setId in prev)) {
        return prev
      }
      const next = { ...prev }
      delete next[setId]
      writeStoredPersonaOverrides(next)
      return next
    })
  }, [])

  const activeSet = useMemo<DemoSet | null>(
    () => findDemoSetById(activeSetId),
    [activeSetId],
  )

  const activePersona = useMemo<PersonaOverride | null>(() => {
    if (!activeSet) {
      return null
    }
    return personaOverrides[activeSet.id] ?? activeSet.userPersona ?? null
  }, [activeSet, personaOverrides])

  // Demo sessions are scoped to the active set: switching prospects shows a
  // different history, but the underlying storage retains all of them.
  const demoSessions = useMemo<DemoSession[]>(() => {
    if (!activeSet) {
      return []
    }
    return allSessions
      .filter((s) => s.setId === activeSet.id)
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
  }, [allSessions, activeSet])

  const value = useMemo<DemoModeContextValue>(
    () => ({
      isDemoMode: enabled && isAllowed && activeSet !== null,
      isAllowed,
      availableSets: DEMO_SETS,
      activeSet,
      activeSetId,
      demoSessions,
      totalDemoSessionCount: allSessions.length,
      activePersona,
      personaOverrides,
      setEnabled,
      setActiveSetId,
      addDemoSession,
      removeDemoSession,
      updateDemoSessionMessages,
      clearDemoSessions,
      setPersonaOverride,
      clearPersonaOverride,
    }),
    [
      enabled,
      isAllowed,
      activeSet,
      activeSetId,
      demoSessions,
      allSessions.length,
      activePersona,
      personaOverrides,
      setEnabled,
      setActiveSetId,
      addDemoSession,
      removeDemoSession,
      updateDemoSessionMessages,
      clearDemoSessions,
      setPersonaOverride,
      clearPersonaOverride,
    ],
  )

  return (
    <DemoModeContext.Provider value={value}>
      {children}
    </DemoModeContext.Provider>
  )
}
